const normalizeStringList = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  const seen = new Set();
  return values
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (typeof item?.query === 'string') return item.query.trim();
      if (typeof item?.text === 'string') return item.text.trim();
      if (typeof item?.domain === 'string') return item.domain.trim();
      return '';
    })
    .filter((item) => {
      if (!item || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
};

const normalizeWebSearchSource = (source = {}) => {
  if (!source || typeof source !== 'object') return null;
  const url = source.url || source.uri || source.link || '';
  if (!url) return null;
  return {
    url,
    title: source.title || source.name || '',
    type: source.type || source.source_type || '',
  };
};

const normalizeWebSearchAction = (action = {}) => {
  if (!action || typeof action !== 'object') return null;
  const queries = normalizeStringList(
    action.queries || action.query || action.search_queries || action.search_query
  );
  const domains = normalizeStringList(action.domains || action.allowed_domains);
  const sources = Array.isArray(action.sources)
    ? action.sources.map(normalizeWebSearchSource).filter(Boolean)
    : [];

  return {
    type: action.type || 'search',
    ...(queries.length > 0 ? { query: queries[0], queries } : {}),
    ...(domains.length > 0 ? { domains } : {}),
    ...(action.url ? { url: action.url } : {}),
    ...(action.title ? { title: action.title } : {}),
    ...(action.pattern ? { pattern: action.pattern } : {}),
    ...(sources.length > 0 ? { sources } : {}),
  };
};

const normalizeWebSearchCall = (call = {}) => {
  if (!call || typeof call !== 'object') return null;
  const id = call.id || call.item_id || call.itemId || null;
  const action = normalizeWebSearchAction(call.action);
  return {
    id: id || `web-search-${call.output_index || 'call'}`,
    type: 'web_search_call',
    status: call.status || 'in_progress',
    ...(action ? { action } : {}),
  };
};

const getWebSearchCalls = (meta = {}) => {
  const calls = [];
  if (Array.isArray(meta.web_search_calls)) {
    calls.push(...meta.web_search_calls.map(normalizeWebSearchCall).filter(Boolean));
  }
  const singleCall = normalizeWebSearchCall(meta.web_search_call);
  if (singleCall) calls.push(singleCall);
  return calls;
};

const mergeWebSearchCalls = (currentCalls = [], incomingCalls = []) => {
  const byId = new Map(
    (Array.isArray(currentCalls) ? currentCalls : [])
      .map(normalizeWebSearchCall)
      .filter(Boolean)
      .map((call) => [call.id, call])
  );

  incomingCalls.forEach((call) => {
    const previous = byId.get(call.id) || {};
    const merged = {
      ...previous,
      ...call,
      action: {
        ...(previous.action || {}),
        ...(call.action || {}),
      },
    };
    if (!Object.keys(merged.action || {}).length) {
      delete merged.action;
    }
    byId.set(call.id, merged);
  });

  return Array.from(byId.values());
};

export const applyAssistantStreamMeta = ({
  meta,
  assistantMessage,
  chatId,
  onMessageSent,
  isWebSearching,
  imageToolName = 'image_generation',
}) => {
  if (!meta || typeof meta !== 'object') return isWebSearching;

  let nextIsWebSearching = isWebSearching;
  let didUpdate = false;

  if (meta.web_search_in_progress === true && !nextIsWebSearching) {
    nextIsWebSearching = true;
    assistantMessage.isWebSearching = true;
    assistantMessage.webSearchStatus = 'searching';
    didUpdate = true;
  } else if (meta.web_search_in_progress === false && nextIsWebSearching) {
    nextIsWebSearching = false;
    assistantMessage.isWebSearching = false;
    assistantMessage.webSearchStatus = 'completed';
    didUpdate = true;
  }

  const normalizedWebSearchStatus =
    meta.web_search_status === 'in_progress' ? 'searching' : meta.web_search_status;
  if (normalizedWebSearchStatus && normalizedWebSearchStatus !== assistantMessage.webSearchStatus) {
    assistantMessage.webSearchStatus = normalizedWebSearchStatus;
    didUpdate = true;
  }

  const incomingWebSearchCalls = getWebSearchCalls(meta);
  if (incomingWebSearchCalls.length > 0) {
    assistantMessage.webSearchCalls = mergeWebSearchCalls(
      assistantMessage.webSearchCalls,
      incomingWebSearchCalls
    );
    didUpdate = true;
  }

  if (meta.image_generation_in_progress === true) {
    assistantMessage.isImageGenerating = true;
    assistantMessage.imageGenerationStatus = 'in_progress';
    assistantMessage.toolError = false;
    assistantMessage.toolName = imageToolName;
    didUpdate = true;
  } else if (meta.image_generation_in_progress === false) {
    assistantMessage.isImageGenerating = false;
    if (meta.image_generation_status) {
      assistantMessage.imageGenerationStatus = meta.image_generation_status;
    }
    didUpdate = true;
  }

  if (didUpdate) {
    onMessageSent({ ...assistantMessage }, chatId);
  }

  return nextIsWebSearching;
};
